// apps/admin/src/api/queries/adminUserKeys.ts
//
// Phase 8.2 Wave 7 Task 49 — TanStack Query key factory for the Admin
// Users surface (System > Users sub-menu). Mirrors the financeKeys.ts
// hierarchy so callers can invalidate at the right granularity:
//
//   ['admin','users']                       — wipe everything
//   ['admin','users','list']                — all list slices
//   ['admin','users','list', filters]       — one filter fingerprint
//   ['admin','users','detail', id]          — one row drawer (task-50)
//   ['admin','users','activity', id]        — activity timeline (task-50)
//
// Pure factory — no hooks, no axios. Safe to import from any layer
// (queries, mutations, tests).
//
// vg-binding: PLAN:task-49
// vg-binding: P8.D-474   — invalidate list after invite/create to surface
//                           the new audit row + status flip

import type { AdminUserListFilters } from '../admin-users.api.js';

/** Hierarchical query-key factory — read-only tuples for stable cache fingerprints. */
export const adminUserKeys = {
  /** Root — invalidates every Admin Users cache slice. */
  all: ['admin', 'users'] as const,

  /** All list slices regardless of filter. */
  lists: () => ['admin', 'users', 'list'] as const,

  /** One filter fingerprint — the canonical cache slice for the table. */
  list: (filters: AdminUserListFilters) =>
    ['admin', 'users', 'list', filters] as const,

  /** All detail slices regardless of id. */
  details: () => ['admin', 'users', 'detail'] as const,

  /** One row drawer / edit page (consumed by task-50). */
  detail: (id: string) => ['admin', 'users', 'detail', id] as const,

  /** Activity sticky-header data (consumed by task-50). */
  activity: (id: string) => ['admin', 'users', 'activity', id] as const,
} as const;

export type AdminUserListQueryKey = ReturnType<typeof adminUserKeys.list>;
export type AdminUserDetailQueryKey = ReturnType<typeof adminUserKeys.detail>;
